import React, { useState, useEffect } from "react";
import axios from "axios";
import { Share2, Link2, Linkedin, Twitter, Send, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { SHARE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countShare = () => {
  // Cookieless, event-only share counter. Best-effort — never blocks the UX.
  axios.post(`${API}/share`).catch(() => {});
};

const encoded = {
  url: encodeURIComponent(SHARE.url),
  text: encodeURIComponent(SHARE.text),
  textUrl: encodeURIComponent(`${SHARE.text} ${SHARE.url}`),
};

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${encoded.textUrl}` },
  { key: "x", label: "X", Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encoded.text}&url=${encoded.url}` },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded.url}` },
  { key: "telegram", label: "Telegram", Icon: Send, href: `https://t.me/share/url?url=${encoded.url}&text=${encoded.text}` },
];

const triggerStyle = { backgroundColor: "transparent", border: "1px solid #2A2233", color: "#F4EEF7" };
const triggerClass =
  "btn-sharp inline-flex items-center gap-3 px-5 py-4 text-xs uppercase tracking-[0.22em] font-medium";
const onEnter = (e) => (e.currentTarget.style.borderColor = "#C724B1");
const onLeave = (e) => (e.currentTarget.style.borderColor = "#2A2233");

export const ShareButton = ({ testid = "share-btn", quiet = false }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasNative, setHasNative] = useState(false);

  const tClass = quiet
    ? "inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-light transition-colors duration-300"
    : triggerClass;
  const tStyle = quiet ? { backgroundColor: "transparent", color: "#9B8FAC" } : triggerStyle;
  const tEnter = quiet ? (e) => (e.currentTarget.style.color = "#F4EEF7") : onEnter;
  const tLeave = quiet ? (e) => (e.currentTarget.style.color = "#9B8FAC") : onLeave;
  const iconColor = quiet ? "#9B8FAC" : "#F4EEF7";
  const iconSize = quiet ? 15 : 18;

  useEffect(() => {
    setHasNative(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  const nativeShare = async () => {
    try {
      await navigator.share({ title: SHARE.title, text: SHARE.text, url: SHARE.url });
      countShare();
    } catch (e) {
      // User cancelled — do not count, do not error.
    }
  };

  const openChannel = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
    countShare();
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE.url);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = SHARE.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (err) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    countShare();
    toast.success("Link copied 🖤");
    setTimeout(() => setCopied(false), 1600);
    setOpen(false);
  };

  // Native share (mobile & supported browsers) — no popover needed.
  if (hasNative) {
    return (
      <button
        data-testid={testid}
        onClick={nativeShare}
        aria-label="Share VYNLWORLD"
        className={tClass}
        style={tStyle}
        onMouseEnter={tEnter}
        onMouseLeave={tLeave}
      >
        <Share2 size={iconSize} strokeWidth={2} color={iconColor} />
        <span>Share</span>
      </button>
    );
  }

  // Fallback: brand-styled popover with channels + copy link.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          aria-label="Share VYNLWORLD"
          className={tClass}
          style={tStyle}
          onMouseEnter={tEnter}
          onMouseLeave={tLeave}
        >
          <Share2 size={iconSize} strokeWidth={2} color={iconColor} />
          <span>Share</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        data-testid="share-popover"
        className="w-56 p-2 rounded-none"
        style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233", color: "#F4EEF7" }}
      >
        <div className="text-[10px] uppercase tracking-[0.28em] px-3 pt-2 pb-3" style={{ color: "#9B8FAC" }}>
          Share the world
        </div>
        <div className="flex flex-col">
          {CHANNELS.map(({ key, label, Icon, href }) => (
            <button
              key={key}
              data-testid={`share-${key}`}
              onClick={() => openChannel(href)}
              className="flex items-center gap-3 px-3 py-3 text-sm text-left transition-colors duration-200 hover:bg-[#241B31]"
              style={{ color: "#F4EEF7" }}
            >
              <Icon size={16} color="#C724B1" />
              {label}
            </button>
          ))}
          <button
            data-testid="share-copy"
            onClick={copyLink}
            className="flex items-center gap-3 px-3 py-3 text-sm text-left transition-colors duration-200 hover:bg-[#241B31] border-t"
            style={{ color: "#F4EEF7", borderColor: "#2A2233" }}
          >
            {copied ? <Check size={16} color="#F0C420" /> : <Link2 size={16} color="#C724B1" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareButton;
